import { femaleQuestionMedium, maleQuestionMedium, bulbMedium } from '@/assets/icons';
import {
  HAVING_NO_PROBLEMS,
  HAVING_NO_PROBLEMS_SOURCE,
  FORCE_STANDARDS,
  FORCE_STANDARDS_SOURCE,
  WITHOUT_DATA,
  WITHOUT_DATA_SOURCE,
} from './quotes';

const MANAGEMENT_OF_PERFORMANCE = `
<p>
  Le management de la performance par l'amélioration continue doit être centrée sur le client. Le besoin du client doit être la priorité absolue de la création de valeur de l'entreprise.
  Ce client peut être l'entité à laquelle on vend un produit, à l'issue d'une transaction, mais on peut également parler de client interne, c'est-à-dire une autre équipe ou entité au sein de la même entreprise.<br>
  La <strong>performance</strong>, c'est la capacité à atteindre la satisfaction totale de son client quant à la qualité des produits de sortie, et les coûts et délais associés (QCD), de la manière la plus efficace possible.
  Et cela implique la capacité à avoir le processus le plus efficace possible pour répondre au besoin de ce client.
</p>
<br>
<p>
  On retrouve cinq composantes standard dans la plupart des domaines : S pour sécurité, Q pour qualité, C pour coûts, D pour délais et M pour Management ou Motivation.
  La lettre utilisée importe peu à condition que chacun comprenne bien l'usage de ces 5 thématiques. Bien sûr, certains domaines vont nécessiter l'usage d'un sixième
  et même un septième thème, mais attention toutefois à ne pas crouler sous les indicateurs.<br>
  Le <strong>M</strong> de management est le dernier thème cité ici, et on y retrouve souvent des indicateurs pauvres qui permettent seulement de faire du reporting, comme l'absentéisme ou les retards des collaborateurs.
  Et pourtant c'est une clé indispensable à l'amélioration continue.<br>
  C'est le <strong>management</strong> qui va permettre de s'assurer d'une bonne dynamique, d'une appropriation et d'un engagement dans les démarches d'amélioration de l'entreprise.<br>
  Pour dissocier un vrai management de la performance et un simple reporting, les acteurs de la performance, c'est-à-dire les créateurs de valeur sur le terrain, doivent s'approprier les indicateurs, et c'est ces mêmes acteurs qui doivent
  définir les indicateur de management et les objectifs fixés pour s'engager dans la démarche. Cela demande de sortir d'un système pyramidal dans lequel les indicateurs sont définis par au-dessus mais qui entrave la prise d'initiative et l'autonomie.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${FORCE_STANDARDS}"</span>
  <br>
  <span class="author">${FORCE_STANDARDS_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  La mesure
</h2>
<p>
  Clarifions d'abord la notion de <strong>Gemba</strong> : Le Gemba est le lieu où la valeur est créée. On parle aussi de terrain. Dans une usine, le Gemba est l'atelier où les produits sont créés ou travaillés.
  Dans un centre d'appel, le Gemba est le (ou les) étage dédié au personnel répondant au téléphone, etc.<br>
  Or, c'est sur le Gemba que la donnée est mesurée, c'est le premier point dans l'ordre logique de la démarche d'amélioration continue.<br>
  La donnée est la matière première de l'amélioration continue. Elle permet d'être factuel et d'empêcher le jugement, l'approximation ou la supposition. Et grâce à la mesure de ces données, on peut être sûrs que les actions prises
  par les personnes et les équipes ont la bonne portée.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<p>
  Aujourd'hui, trop de décisions sont prises sur la base d'opinions, un bon management de la performance éradique les suppositions et les remplacent par des données tangibles.
  La mesure permet donc de collecter la donnée et d'avoir un regard objectif sur un processus.
</p>

<ul>
  La mesure peut être :
  <li>en temps réel : via un système SPC (Maitrise statistiques des procédés) par exemple qui recueille la donnée en temps réel,</li>
  <li>périodique : toutes les heures, on compte le nombre de pièces réalisées,</li>
  <li>par échantillonnage ou sondage : pour mesurer la satisfaction client par exemple.</li>
</ul>
<p>
  Quel que soit le type de mesure ou sa périodicité, elle va être corrélée à un indicateur précis (ou KPI, Key Performance Indicator).
  Pour déterminer un bon indicateur, il doit être en lien, d'une manière ou d'une autre à la satisfaction du client.<br>
  On peut utiliser deux types d'indicateurs : Les indicateurs de processus et les indicateurs de résultats.
  Ils doivent nous dire si le résultat est conforme et si le processus a été correctement suivi.<br>
  On trouve généralement des indicateurs de résultats dans les entreprises mais les indicateurs de processus sont souvent manquants, bien que ce soit souvent le non-respect d'un processus qui entraîne une non-qualité ou un délai dépassé par exemple.
</p>
<div class="ia-sensei-example">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${bulbMedium}"></path></svg>
  <div class="example-title">Par exemple :</div>
  Nous allons suivre une recette de cuisine pour la confection d'un gâteau. La recette va naturellement nous donner les bons indicateurs de processus que nous pourrons suivre tandis que le résultat de la confection du gâteau est le gâteau lui-même.
  Comme indicateurs de processus, nous pourrons donc choisir la température du four, la durée de cuisson, l'ordre des ingrédients, ... et les indicateurs de résultats pourront être
  la taille et la consistance du gâteau, un sondage auprès des invités, etc.
</div>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Ok, donc la mesure de la donnée, c'est la saisie des indicateurs ?</span>
</div>
<p>
  Ça en fait partie, c'est vrai, mais ce n'est pas uniquement cela. La mesure de la donnée consiste à considérer les choses factuellement, donc les valeurs des indicateurs, bien sûr, mais aussi toutes les choses visibles ou vérifiables par des faits.
  Quand on détecte un problème, on décrit ce que l'on voit, on ne suppose pas, on vérifie avec des données factuelles.
  Quand on fait sa tournée terrain, on ne fait pas d'approximation, on ne juge pas, on voit, on discute et on analyse.
  La mesure permet d'analyser et de décomposer les problèmes.
</p>
<h2 class="basic-sub-title">
  La détection de problèmes
</h2>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">Zut, j'ai encore trouvé un problème ce matin.</span>
</div>
<p>
  C'est parfait ! Vous avez donc l'opportunité de le résoudre et de vous améliorer, en avant !<br>
  Les problèmes sont les meilleures opportunités d'amélioration que vous puissiez avoir. Mais définissons d'abord exactement ce qu'est un problème :<br>
  Un problème est un écart entre la situation actuelle et une situation idéale.<br>
  La situation idéale peut être un standard ou un objectif d'indicateur par exemple. Cela suppose, bien sûr, que les standards et les objectifs soient déjà fixés, d'où les notions préalables de management et de mesure.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Ok, j'ai compris comment remarquer mes problèmes. Mais comment les détecter plus tôt, et comment les anticiper ?</span>
</div>
<p>
  Excellente question. Il existe des outils d'analyse statistique complexes comme les cartes de contrôle par exemple qui permettent de limiter la variabilité des données. Mais ce genre d'outil peut être compliqué à paramétrer.<br>
  Mais il existe un outil bien plus simple et très efficace : la <strong>tournée terrain</strong>.
  La tournée terrain au niveau managérial est un acte essentiel qui permet d'assurer la maitrise des standards par le Gemba et de prévoir les écarts.
  Mais ce n'est pas qu'un outil de manager. Grâce à Shizen, la tournée terrain est à portée de chacun et peut être paramétrée, planifiée et lancée en quelques clics.<br>
  Une tournée terrain  peut même être réalisée à plusieurs pour vérifier les faits, mettre en place des actions conservatoires, identifier des causes racines et mettre en œuvre et standardiser les contre-mesures aux problèmes pour éviter la récurrence.<br>
  Aujourd'hui, certaines sociétés matures dans l'amélioration continue proposent aux opérateurs d'effectuer des tournées terrain quotidiennes de leurs postes de travail.
  Ainsi, le personnel est plus impliqué dans la démarche, gagne en autonomie et interagit plus régulièrement avec les autres équipes et niveaux de management pour la résolution de ses problèmes.
</p>
<h2 class="basic-sub-title">
  Alerter et agir
</h2>
<p>
  On a bien compris comment détecter les problèmes et on sait ce que l'on peut mettre en place pour avoir une longueur d'avance.
  Il faut maintenant prendre les bonnes mesures en fonction de la situation. C'est tout l'intérêt d'avoir des équipes autonomes sur le terrain.<br>
  Mais certaines situations, certains problèmes ne peuvent être résolus sans aide ou soutien.
  Il s'agit là de faire appel à la bonne personne ou à la bonne équipe pour prendre en charge le problème qu'une personne a détecté.<br>
  Shizen permet d'alerter ces personnes le plus vite possible afin de soutenir la création de valeur sur le terrain.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">Et quand passons-nous à l'action ?</span>
</div>
<p>
  Pas forcément tout de suite : Certains problèmes vont vous pousser à mettre en place des actions conservatoires ou actions de sécurisation.<br>
  Si un tuyau de vidange fuit dans tout l'atelier, il faut évidemment trouver un moyen pour éviter l'inondation. Mais cette action ne fait pas partie de la résolution du problème.<br><br>
  Avant de résoudre le problème, il est nécessaire de le comprendre parfaitement et d'être sûr que l'on peut pointer du doigt le vrai coupable.
  C'est tout l'intérêt des outils 5 pourquoi et Ishikawa grâce auxquels chacun peut identifier les causes racines des problèmes.<br>
  Lorsque l'on a effectivement trouvé le coupable, c'est-à-dire la (ou les) cause racine, on peut déterminer les meilleures actions à mettre en place pour éradiquer le problème.
  Lorsque ces actions sont définies, il faut les assigner aux personnes compétentes et les planifier.<br>
  Enfin, les actions sont réalisées et les solutions doivent être standardisées pour empêcher la récurrence du problème.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Et donc, comment peut-on être sûr que le problème ne reviendra plus ?</span>
</div>
<p>
  En suivant vos réunions rituelles quotidiennes, vos tournées terrain et en monitorant vos indicateurs :<br>
  Les rituels sont aussi des moments privilégiés pour alerter et faire remonter les problèmes.
  Ces points quotidiens permettent de faire l'état des lieux des indicateurs de performance, des derniers problèmes détectés et des actions en cours.
  Un rituel doit rassembler plusieurs équipes, notamment le personnel terrain, le management et les fonctions supports.
  Les services de maintenance et de qualité peuvent également participer aux rituels opérationnels pour favoriser un échange rythmé et régulier entre les services, et assurer une meilleure compréhension des indicateurs.<br>
  L'enjeu des liens inter-équipes et inter-niveaux est de conserver une dynamique de réaction aux problèmes. Il arrive que les problèmes ne puissent pas être résolus par une équipe terrain, auquel cas
  elle fera appel à une équipe de support ou au management pour la résoudre. Sans lien et sans système de management comme Shizen, l'équipe n'a aucun suivi sur le traitement et la résolution de son problème.
  Le manque d'information engendrera une frustration et un sentiment d'abandon qui poussera les collaborateurs à arrêter de remonter des problèmes.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${HAVING_NO_PROBLEMS}"</span>
  <br>
  <span class="author">${HAVING_NO_PROBLEMS_SOURCE}</span>
</div>
<p>
  <br>
  Le management de la performance est un enchainement de ces concepts clés : la mesure et la compréhension de la donnée factuelle, la capacité à analyser et à détecter les écarts
  et enfin de bons processus pour alerter et agir au bon niveau.
  Shizen fournit un système de management de la performance complet permettant de piloter et monitorer la performance à chaque niveau hiérarchique.
  Néo vous accompagne dans chaque facette du système pour vous aider ou vous guider sur toutes les thématiques.
</p>
`;

const MANAGEMENT_OF_PERFORMANCE_TITLE = 'Le management de la performance';

export default {
  MANAGEMENT_OF_PERFORMANCE,
  MANAGEMENT_OF_PERFORMANCE_TITLE,
};
